import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fcfa } from "@/lib/format";
import { purposeLabel } from "@/lib/i18n";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Eyebrow, Logo, SimBanner, StatusBadge } from "@/components/common";

export default function SimulatedCheckout() {
  const { sid } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [p, setP] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/payments/session/${sid}`);
    setP(data);
    return data;
  };

  useEffect(() => {
    load().catch((e) => setError(errMsg(e)));
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  const confirm = async (succeeded) => {
    setError("");
    try {
      await api.post(`/payments/simulated/${sid}/confirm`, { succeeded });
      setWaiting(true);
      timer.current = setInterval(async () => {
        const d = await load();
        if (!["initie", "en_attente_validation"].includes(d.status)) {
          clearInterval(timer.current);
          setWaiting(false);
          await refresh();
        }
      }, 1200);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const final = p && ["valide", "echoue", "rejete"].includes(p.status);
  const goHome = async () => {
    const me = await refresh();
    if (p?.operation_id) return nav(`/app/operations/${p.operation_id}?tab=paiements`, { replace: true });
    nav(homeFor(me), { replace: true });
  };

  return (
    <div className="min-h-screen mr-grid-bg">
      <header className="max-w-md mx-auto px-5 h-16 flex items-center"><Logo /></header>
      <main className="max-w-md mx-auto px-5 py-8 mr-fade-up">
        <Eyebrow className="mb-4">Paiement {p?.provider_label || ""}</Eyebrow>
        <SimBanner>Mode simulé : aucun argent n'est débité. La validation passe par un webhook signé, comme en production.</SimBanner>
        {p && (
          <div className="mr-card mt-6 space-y-4" data-testid="checkout-card">
            <div className="flex justify-between items-center">
              <span className="text-sm text-mr-text2">{purposeLabel(p.purpose)}</span>
              <StatusBadge status={p.status} testId="checkout-status" />
            </div>
            <p className="font-display font-bold text-4xl tabular" data-testid="checkout-amount">{fcfa(p.amount)}</p>
            <p className="text-sm text-mr-muted">Bénéficiaire : <span className="text-mr-text">{p.account_holder}</span></p>
            {!final && !waiting && (
              <div className="grid gap-3 pt-2">
                <button data-testid="checkout-confirm-button" className="mr-btn-primary" onClick={() => confirm(true)}>Confirmer le paiement</button>
                <button data-testid="checkout-fail-button" className="mr-btn-secondary" onClick={() => confirm(false)}>Simuler un échec</button>
              </div>
            )}
            {waiting && (
              <p className="flex items-center gap-2 text-sm text-mr-text2" data-testid="checkout-waiting"><Loader2 className="animate-spin" size={16} /> En attente de la confirmation de {p.provider_label}…</p>
            )}
            {final && (
              <div className="pt-2 space-y-4" data-testid="checkout-result">
                <p className="flex items-center gap-2 text-sm">
                  {p.status === "valide" ? <CheckCircle2 className="text-mr-success" size={18} /> : <XCircle className="text-mr-danger" size={18} />}
                  {p.status === "valide" ? `Paiement confirmé par ${p.provider_label}. Merci !` : "Le paiement n'a pas abouti."}
                </p>
                <button data-testid="checkout-continue-button" className="mr-btn-primary w-full" onClick={goHome}>Continuer</button>
              </div>
            )}
          </div>
        )}
        {error && <p className="text-sm text-mr-danger mt-4" data-testid="checkout-error">{error}</p>}
      </main>
    </div>
  );
}
